/**
 * index.tsx
 *
 * Author: Maurice T. Meyer
 * E-Mail: maurice@lavireo.com
 *
 * (c) Laviréo. All rights reserved.
 *
 * This document is the property of Laviréo.
 * It is considered confidential and proprietary.
 *
 * This document may not be reproduced or transmitted in any form,
 * in whole or in part, without the express written permission of
 * Laviréo.
 */

import { ChangeEvent, FC, useEffect, useMemo, MouseEvent } from 'react';
import { useField }                            from 'formik';
import usePlacesAutocomplete, { getGeocode }   from 'use-places-autocomplete';
import { Loader }                              from '@googlemaps/js-api-loader';
import { useConfig }                           from '~/hooks/useConfig';
import { Input }                               from '~/components/input';
import { styles }                              from './styles';

export const AddressForm: FC = () => {
  const { key: apiKey } = useConfig();
  if (!apiKey) {
    throw new Error('API key is not set');
  }

  const {
    init,
    value,
    setValue,
    clearSuggestions,
    suggestions: { status, data }
  } = usePlacesAutocomplete({
    debounce: 300,
    initOnMount: false,
    requestOptions: {
      types: ['address'],
      language: 'de',
      region: 'DE',
      componentRestrictions: { country: ['de'] },
    },
  });

  const loader = useMemo(() => new Loader({
    apiKey,
    version: 'weekly',
    libraries: ['places']
  }), [apiKey]);

  useEffect(() => {
    loader.importLibrary('places').then(
      () => init(),
      err => console.error('Error loading Google Maps API:', err)
    );
  }, [init, loader]);

  const [field, meta, helpers] = useField({
    name: 'address',
    type: 'text'
  });

  const handleInput = (ev: ChangeEvent<HTMLInputElement>) => {
    setValue(ev.target.value);
    helpers.setValue(ev.target.value, );
  };

  const handleSelect = (ev: MouseEvent, address: string, placeId: string) => {
    ev.preventDefault();
    setValue(address, false);
    helpers.setValue(address, false);
    clearSuggestions();

    getGeocode({ placeId, language: 'de' }).then((results) => {
      helpers.setValue(results[0].formatted_address, false);
      setValue(results[0].formatted_address, false);
    });
  }

  return (
    <div css={styles.container}>
      <Input {...field} type="text" value={value} label="Adresse Ihrer Immobilie" placeholder="Straße & Nr., PLZ, Ort" onChange={handleInput} />
      { data.length > 0 &&
        <div css={styles.options}>
          <ul>
          {status === 'OK' && data.map(({ place_id, description, ...rest }) => (
            <li
              key={place_id}
              css={styles.option}
              onClick={ev => handleSelect(ev, description, place_id)}
            >
              {description}
            </li>
          ))}
          </ul>
        </div>
      }
    </div>
  )
}
